import { STAFF_PATHS } from '../utils/staffPaths';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { isAdminAuthenticated, removeAdminToken } from '../services/authService';
import { staffJSON, requireStaffProfile } from './staffApi';
import StaffDesign from './StaffDesign';

const StaffContext = createContext(null);
export const useInternalStaff = () => useContext(StaffContext);

export default function InternalStaffRoute({ adminOnly = false, children }) {
  const [staff, setStaff] = useState(null);
  const [setupPrompt, setSetupPrompt] = useState(null);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [retry, setRetry] = useState(0);
  const authenticated = isAdminAuthenticated();
  useEffect(() => {
    if (!authenticated) return undefined;
    let active = true;
    let pending = false;
    const controller = new AbortController();
    const check = async () => {
      if (pending) return;
      pending = true;
      try {
        const data = requireStaffProfile(await staffJSON('/me', { signal: controller.signal }));
        if (active) { setStaff(data); setSetupPrompt(previous => previous === null ? data.mfa_setup_required === true : previous); setError(''); }
      } catch (err) {
        if (!active || err.name === 'AbortError') return;
        setError(err.message);
        if (err.status === 401 || err.status === 403) { removeAdminToken(); setExpired(true); }
      } finally { pending = false; }
    };
    check();
    const timer = setInterval(check, 60000);
    window.addEventListener('focus', check);
    return () => { active = false; controller.abort(); clearInterval(timer); window.removeEventListener('focus', check); };
  }, [authenticated, retry]);
  if (!authenticated || expired) return <Navigate to={STAFF_PATHS.login} state={{ staffSignInError: error }} replace />;
  if (error) return <StaffDesign className="staff-enrollment"><main style={{ padding: 32 }} role="alert">{error} <button onClick={() => setRetry(value => value + 1)}>Try again</button><button onClick={() => { removeAdminToken(); setExpired(true); }}>Return to sign in</button></main></StaffDesign>;
  if (!staff) return <StaffDesign className="staff-enrollment"><main style={{ padding: 32 }} role="status">Checking staff access…</main></StaffDesign>;
  if (adminOnly && staff.role !== 'admin') return <Navigate to={STAFF_PATHS.marketing} replace />;
  if (!['admin', 'marketing'].includes(staff.role)) return <Navigate to={STAFF_PATHS.login} replace />;
  return <StaffContext.Provider value={{ ...staff, mfa_setup_prompt: setupPrompt,
    completeMfaSetup: () => { setSetupPrompt(false); setRetry(value => value + 1); } }}>{children}</StaffContext.Provider>;
}
